import { ErrorMessage, type FormikContextType, useFormikContext } from "formik";
import { get } from "lodash";
import { useEffect, useRef, useState } from "react";
import {
  type OptionsOrGroups,
  type GroupBase,
  type ActionMeta,
  type OnChangeValue,
  type MultiValue,
  type SingleValue,
  type StylesConfig,
} from "react-select";
import { AsyncPaginate, type LoadOptions } from "react-select-async-paginate";
import {
  type PaginatedResults,
  type PaginationRequest,
} from "shared/api/types/pagination";
import PillarFormInputWrapper, {
  type PillarFormInputWrapperProps,
} from "shared/components/pillar-form/components/PillarFormInputWrapper";
import { type FiltersRequest } from "shared/filter-where-clause";

export type PillarFormProfileSelectValueType<ValueType> = {
  value: ValueType;
  label: string;
};
export type PillarFormProfileSelectProps<ENTITYTYPE, ValueType, FormType> =
  PillarFormInputWrapperProps & {
    queryCallback?: (
      filters: FiltersRequest,
      pagination: PaginationRequest,
    ) => Promise<PaginatedResults<ENTITYTYPE, any>>;

    onChangeCallback?: (
      newValue: OnChangeValue<
        PillarFormProfileSelectValueType<ValueType>,
        boolean
      >,
      actionMeta: ActionMeta<PillarFormProfileSelectValueType<ValueType>>,
      queryResults: ENTITYTYPE[],
      formikContext: FormikContextType<FormType>,
    ) => void;

    labelCallback: (
      entity: ENTITYTYPE,
      queryResponse?: PaginatedResults<ENTITYTYPE>,
    ) => string;

    valueCallback: (
      entity: ENTITYTYPE,
      queryResponse?: PaginatedResults<ENTITYTYPE>,
    ) => ValueType;

    additionalOptionsEndOfListCallback?: (
      queryResponse?: PaginatedResults<ENTITYTYPE>,
    ) => PillarFormProfileSelectValueType<ValueType>[];

    additionalOptionsStartOfListCallback?: (
      queryResponse?: PaginatedResults<ENTITYTYPE>,
    ) => PillarFormProfileSelectValueType<ValueType>[];

    placeholder?: string;
    additionalClasses?: string;
    presetOptions?: PillarFormProfileSelectValueType<ValueType>[];
    presetValueCallback?: (
      value:
        | PillarFormProfileSelectValueType<ValueType>[]
        | PillarFormProfileSelectValueType<ValueType>,
    ) => Promise<PillarFormProfileSelectValueType<ValueType>[]>;
    pageSize?: number;
    readOnly?: boolean;
    isMultiple?: boolean;
    cacheKey?: string[];
    selectedValuesKey?: string;
    isDisabled?: boolean;
    allowCreate?: boolean;
  };

const PillarSelectComponent = <ENTITYTYPE, ValueType, FormType>({
  name,
  label,
  labelClassName,
  additionalClasses,
  queryCallback,
  onChangeCallback,
  labelCallback,
  valueCallback,
  additionalOptionsEndOfListCallback,
  additionalOptionsStartOfListCallback,
  placeholder = "Search...",
  pageSize = 25,
  isMultiple = false,
  isDisabled = false,
  allowCreate = false,
  selectedValuesKey,
  presetOptions,
  presetValueCallback,
  cacheKey,
  testid,
  readOnly,
  ...props
}: PillarFormProfileSelectProps<ENTITYTYPE, ValueType, FormType>) => {
  const [queryResults, setQuryResults] = useState<ENTITYTYPE[]>([]);
  const [fieldOptions, setFieldOptions] = useState<
    PillarFormProfileSelectValueType<ValueType>[]
  >(presetOptions || []);
  const [valueState, setValueState] = useState<
    | MultiValue<PillarFormProfileSelectValueType<ValueType>>
    | SingleValue<PillarFormProfileSelectValueType<ValueType>>
    | undefined
  >();
  const [presetValuesLoading, setPresetValuesLoading] =
    useState(!!presetValueCallback);
  const formikContext = useFormikContext<FormType>();

  const { setFieldValue, values } = formikContext;

  useEffect(() => {
    let value = get(values, name);

    if (presetValuesLoading) {
      presetValueCallback?.(value || []).then((presetValues) => {
        setFieldOptions(presetValues);
        setPresetValuesLoading(false);
      });
    }
    if (!presetValuesLoading) {
      if (isMultiple && Array.isArray(value)) {
        value = (value as unknown[])
          .map((item: unknown) =>
            fieldOptions.find((option) => option?.value === item),
          )
          .filter(
            (option): option is PillarFormProfileSelectValueType<ValueType> =>
              !!option,
          );
      } else {
        value = fieldOptions.find((option) => option?.value === value) || null;
      }

      setValueState(value);
    }
  }, [presetValuesLoading]);

  const onChange = (
    newValue: OnChangeValue<
      PillarFormProfileSelectValueType<ValueType>,
      boolean
    >,
    actionMeta: ActionMeta<PillarFormProfileSelectValueType<ValueType>>,
  ) => {
    if (onChangeCallback) {
      onChangeCallback(newValue, actionMeta, queryResults, formikContext);
    }

    // Update valueState directly
    setValueState(newValue);

    if (isMultiple) {
      if (Array.isArray(newValue)) {
        setFieldValue(
          name,
          newValue.map((option) => option.value),
        );

        if (selectedValuesKey) {
          setFieldValue(selectedValuesKey, newValue);
        }
      } else {
        setFieldValue(name, []);
      }
    } else {
      if (newValue && "value" in newValue) {
        setFieldValue(name, newValue.value);

        if (selectedValuesKey) {
          setFieldValue(selectedValuesKey, newValue);
        }
      } else {
        setFieldValue(name, null);
      }
    }
  };

  const loadOptions: LoadOptions<
    PillarFormProfileSelectValueType<ValueType>,
    GroupBase<PillarFormProfileSelectValueType<ValueType>>,
    number
  > = async (
    inputValue: string,
    options: OptionsOrGroups<
      PillarFormProfileSelectValueType<ValueType>,
      GroupBase<PillarFormProfileSelectValueType<ValueType>>
    >,
    page = 1,
  ) => {
    if (!queryCallback) {
      return {
        options: fieldOptions,
        hasMore: false,
        additional: page + 1,
      };
    }
    const queryResponse: PaginatedResults<ENTITYTYPE> = await queryCallback(
      {
        text: inputValue,
      },
      {
        page,
        pageSize,
      },
    );
    setQuryResults([...queryResults, ...queryResponse.results]);

    const responseOptions: PillarFormProfileSelectValueType<ValueType>[] =
      queryResponse.results.map((entity) => ({
        value: valueCallback(entity, queryResponse),
        label: labelCallback(entity, queryResponse),
      }));

    const additionalOptionsEndOfList =
      additionalOptionsEndOfListCallback?.(queryResponse) || [];

    const additionalOptionsStartOfList =
      additionalOptionsStartOfListCallback?.(queryResponse) || [];

    let formattedOptions: PillarFormProfileSelectValueType<ValueType>[] = [
      ...additionalOptionsStartOfList,
      ...responseOptions,
      ...additionalOptionsEndOfList,
    ];
    // If allowed, and the user typed a value not already present, add a "Create" option
    const typed = inputValue?.trim();
    if (allowCreate && typed) {
      const exists = formattedOptions.some((opt) => {
        try {
          return (
            String(opt.value) === typed ||
            (opt.label || "").trim().toLowerCase() === typed.toLowerCase()
          );
        } catch (e) {
          return false;
        }
      });

      if (!exists) {
        const createOption: PillarFormProfileSelectValueType<ValueType> = {
          value: typed as unknown as ValueType,
          label: `Create "${typed}"`,
        };
        // prepend create option so it's easy to select after typing
        formattedOptions = [createOption, ...formattedOptions];
      }
    }
    setFieldOptions(formattedOptions);
    const hasMore =
      queryResponse.page < Math.ceil(queryResponse.totalResults / pageSize);
    return {
      options: formattedOptions,
      hasMore,
      additional: page + 1,
    };
  };

  // Outside a modal the menu renders inline with a high z-index, which keeps it
  // above sticky headers and keeps its options inside the select's DOM. Inside
  // a modal it is portaled to document.body instead: the dialog panel carries
  // a CSS transform and overflow clipping, and no z-index escapes those.
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const [menuPortalTarget, setMenuPortalTarget] = useState<HTMLElement>();
  useEffect(() => {
    const container = selectContainerRef.current;
    if (container?.closest("[role=dialog]")) {
      setMenuPortalTarget(container.ownerDocument.body);
    }
  }, []);

  return (
    <PillarFormInputWrapper
      label={label}
      labelClassName={labelClassName}
      name={name}
      additionalClasses={additionalClasses}
      testid={testid ? `${testid}-select` : undefined}
      {...props}
    >
      <div data-testid={testid || name} ref={selectContainerRef}>
        <AsyncPaginate
          cacheUniqs={cacheKey}
          loadOptions={loadOptions}
          debounceTimeout={500}
          onChange={onChange}
          value={valueState}
          placeholder={placeholder}
          isMulti={isMultiple}
          isDisabled={isDisabled || readOnly}
          isLoading={presetValuesLoading}
          menuPortalTarget={menuPortalTarget}
          classNames={{
            menu: () => `${name}-options`,
            option: () => `${name}-option`,
          }}
          styles={{
            control: (baseStyles: StylesConfig) => ({
              ...baseStyles,
              borderRadius: "5px",
              border: "1px solid #ccc",
            }),
            option: (baseStyles: StylesConfig) => ({
              ...baseStyles,
              cursor: "pointer",
            }),
            menu: (baseStyles: StylesConfig) => ({
              ...baseStyles,
              backgroundColor: "#f0f0f0",
              zIndex: 100,
            }),
            menuPortal: (baseStyles: StylesConfig) => ({
              ...baseStyles,
              zIndex: 100,
            }),
          }}
        />
      </div>
      <ErrorMessage
        component="span"
        className="text-danger-small"
        name={name}
      />
    </PillarFormInputWrapper>
  );
};

export default PillarSelectComponent;
